import {
  CheckCircle2,
  Circle,
  Loader2,
  ListChecks,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useDevAgentStore, type FeedItem as FeedItemT } from "@/stores/devAgentStore";

export function InlinePlanCard({
  item,
  onSendMessage,
  disabled,
}: {
  item: FeedItemT;
  onSendMessage?: (msg: string, statusHint?: string) => void;
  disabled?: boolean;
}) {
  const steps: any[] = item.data.steps || [];
  const summary: string = item.data.summary || "Plan";
  const isApproved = !!item.data.isApproved;
  const currentStep = item.data.currentStep || 0;
  const allPending = steps.every((s: any) => s.status === "pending");
  const allDone = steps.every(
    (s: any) => s.status === "completed" || s.status === "done",
  );
  const anyRunning = steps.some((s: any) => s.status === "running");
  const showInitialApprove = !isApproved && allPending && !anyRunning;
  const nextPendingIdx = steps.findIndex((s: any) => s.status === "pending");
  const showNextStep =
    isApproved &&
    !anyRunning &&
    !allDone &&
    nextPendingIdx >= 0 &&
    currentStep > 0;

  const executeStep = (stepIdx: number) => {
    const step = steps[stepIdx];
    if (!step) return;
    const store = useDevAgentStore.getState();
    const updated = [...steps];
    updated[stepIdx] = { ...updated[stepIdx], status: "running" };
    store.updateFeedItem(item.id, { steps: updated, currentStep: stepIdx + 1 });
    const stepContext = steps
      .slice(0, stepIdx)
      .filter(
        (s: any) => s.status === "completed" || s.status === "done",
      )
      .map(
        (s: any) => `Step ${s.step_number} (done): ${s.description}`,
      )
      .join("\n");
    const msg = `Execute step ${step.step_number}: ${step.description}${
      stepContext ? `\n\nCompleted so far:\n${stepContext}` : ""
    }`;
    onSendMessage?.(msg, `Executing step ${step.step_number}...`);
  };

  const handleApprove = () => {
    const store = useDevAgentStore.getState();
    store.updateFeedItem(item.id, { isApproved: true });
    executeStep(0);
  };

  const handleNextStep = () => {
    const store = useDevAgentStore.getState();
    const updated = [...steps];
    updated.forEach((s: any, i: number) => {
      if (s.status === "running")
        updated[i] = { ...s, status: "completed" };
    });
    store.updateFeedItem(item.id, { steps: updated });
    const nextIdx = updated.findIndex((s: any) => s.status === "pending");
    if (nextIdx >= 0) executeStep(nextIdx);
  };

  const handleReject = () => {
    useDevAgentStore.getState().removeFeedItem(item.id);
    onSendMessage?.("Cancel this plan.", "Plan rejected.");
  };

  return (
    <div className="py-1.5">
      <div className="border border-blue-500/20 bg-blue-500/5 rounded-lg p-3 max-w-[85%] shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="w-4 h-4 text-blue-700 dark:text-blue-400 shrink-0" />
          <span className="text-xs font-semibold text-blue-800 dark:text-blue-400">
            {summary}
          </span>
          {isApproved && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-700 dark:text-green-400 font-medium ml-auto">
              Approved
            </span>
          )}
        </div>

        <div className="space-y-1.5">
          {steps.map((step: any, i: number) => {
            const isDone =
              step.status === "completed" || step.status === "done";
            const isRunning = step.status === "running";
            const isFailed = step.status === "failed";
            return (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="shrink-0 mt-0.5">
                  {isDone ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                  ) : isRunning ? (
                    <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />
                  ) : isFailed ? (
                    <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                  ) : (
                    <Circle className="w-4 h-4 text-muted-foreground/75" />
                  )}
                </span>
                <span
                  className={`shrink-0 font-mono text-[10px] mt-0.5 w-4 text-center ${
                    isDone
                      ? "text-green-600 dark:text-green-400"
                      : isRunning
                        ? "text-blue-600 dark:text-blue-400"
                        : isFailed
                          ? "text-red-600 dark:text-red-400"
                          : "text-muted-foreground"
                  }`}
                >
                  {step.step_number}
                </span>
                <span
                  className={
                    isDone
                      ? "line-through text-green-700/80 dark:text-green-400/70 break-words"
                      : isRunning
                        ? "text-blue-700 dark:text-blue-400 animate-pulse break-words"
                        : isFailed
                          ? "text-red-700 dark:text-red-400 break-words"
                          : "text-muted-foreground break-words"
                  }
                >
                  {step.description}
                </span>
              </div>
            );
          })}
        </div>

        {showInitialApprove && (
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-blue-500/10">
            <button
              type="button"
              onClick={handleApprove}
              disabled={disabled}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Approve &amp; Start Step 1</span>
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={disabled}
              className="flex items-center justify-center gap-1.5 py-1.5 px-3 rounded text-xs font-medium border border-border bg-background text-foreground hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="h-3.5 w-3.5" />
              <span>Reject</span>
            </button>
          </div>
        )}
        {showNextStep && (
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-green-500/10">
            <button
              type="button"
              onClick={handleNextStep}
              disabled={disabled}
              className="flex-1 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Step {steps[nextPendingIdx].step_number}
            </button>
            <button
              type="button"
              onClick={() => {
                onSendMessage?.(
                  "Cancel this plan.",
                  "Stopped at step " + currentStep + ".",
                );
              }}
              disabled={disabled}
              className="py-1.5 px-3 rounded text-xs font-medium border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Stop
            </button>
          </div>
        )}
        {allDone && (
          <div className="mt-2 pt-2 border-t border-green-500/10 text-xs text-green-700 dark:text-green-400 font-medium">
            All {steps.length} steps completed
          </div>
        )}
      </div>
    </div>
  );
}
